import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format, differenceInCalendarDays, eachDayOfInterval, addDays } from "date-fns";
import { api, imgUrl, errMsg, startLogin, fmtDate, money0 } from "@/lib/api";
import { quote } from "@/lib/fees";
import { useAuth } from "@/context/AuthContext";
import { useBookingsOpen, ComingSoon } from "@/components/ComingSoon";
import { Spinner, Notice } from "@/components/RequireAuth";
import { Rating, ReviewItem } from "@/components/Reviews";
import { GuestBreakdown } from "@/components/FeeCompare";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, MapPin, BedDouble, Bath, Users, ShieldCheck, SearchX, Grid2x2 } from "lucide-react";
import { PolicyText } from "@/components/CancellationPolicy";
import { ApproxMap } from "@/components/ApproxMap";

function Gallery({ photos, title }) {
  const [open, setOpen] = useState(false);
  const p = photos.length ? photos : [""];
  const n = Math.min(p.length, 5);
  const tile = (i) => {
    if (i === 0) return `col-span-4 row-span-2 ${n > 1 ? "sm:col-span-2" : ""}`;
    const wide = n === 2 || n === 3 || (n === 4 && i === 3);
    return `hidden sm:block ${wide ? "sm:col-span-2" : ""} ${n === 2 ? "sm:row-span-2" : ""}`;
  };
  return (
    <>
      <div className="relative grid h-[280px] grid-cols-4 grid-rows-2 gap-2 overflow-hidden rounded-3xl sm:h-[460px]" data-testid="listing-gallery">
        {p.slice(0, 5).map((u, i) => (
          <button key={u || "placeholder"} onClick={() => setOpen(true)} className={`overflow-hidden bg-stone-200 ${tile(i)}`}>
            {u && <img src={imgUrl(u)} alt={title} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />}
          </button>
        ))}
        <Button size="sm" variant="secondary" onClick={() => setOpen(true)} className="absolute bottom-4 right-4 rounded-full bg-white text-stone-900 hover:bg-stone-100" data-testid="gallery-show-all">
          <Grid2x2 className="mr-2 h-4 w-4" /> All photos
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogTitle>{title}</DialogTitle>
          <div className="space-y-3">{p.filter(Boolean).map((u) => <img key={u} src={imgUrl(u)} alt="" className="w-full rounded-xl" />)}</div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const ISO = (d) => format(d, "yyyy-MM-dd");

function bookButtonLabel({ own, user, busy }) {
  if (own) return "This is your listing";
  if (!user) return "Sign in to request";
  return busy ? "Sending…" : "Request to book";
}

function datesLabel(range) {
  if (!range?.from) return "Pick check-in and check-out";
  return `${format(range.from, "MMM d")} → ${range.to ? format(range.to, "MMM d") : "pick check-out"}`;
}

function GuestStepper({ value, max, onChange }) {
  return (
    <div className="mt-4 flex items-center justify-between rounded-2xl border px-4 py-3">
      <span className="text-sm font-medium">Guests <span className="text-stone-400">(max {max})</span></span>
      <span className="flex items-center gap-3">
        <button data-testid="booking-guests-minus" className="h-10 w-10 rounded-full border" onClick={() => onChange(Math.max(1, value - 1))}>−</button>
        <span className="font-mono-num" data-testid="booking-guests-value">{value}</span>
        <button data-testid="booking-guests-plus" className="h-10 w-10 rounded-full border" onClick={() => onChange(Math.min(max, value + 1))}>+</button>
      </span>
    </div>
  );
}

const TermsAgreement = ({ agree, setAgree }) => (
  <label className="mt-4 flex items-start gap-3 text-xs text-stone-600" data-testid="booking-terms-agreement">
    <Checkbox data-testid="booking-terms-checkbox" checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" />
    <span>I agree to the <Link to="/guest-terms" target="_blank" className="font-semibold text-primary hover:underline">Guest Terms</Link> and this home's house rules, and I'm responsible for any damage my group causes.</span>
  </label>
);

function useStayRange(unavailable) {
  const [range, setRange] = useState();
  const taken = useMemo(() => new Set(unavailable), [unavailable]);
  const disabled = useMemo(() => [{ before: new Date() }, (d) => taken.has(ISO(d))], [taken]);
  const nights = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0;
  const clash = nights > 0 && eachDayOfInterval({ start: range.from, end: addDays(range.to, -1) }).some((d) => taken.has(ISO(d)));
  return { range, setRange, disabled, nights, clash };
}

function BookingCard({ l }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { range, setRange, disabled, nights, clash } = useStayRange(l.unavailable_dates);
  const [guests, setGuests] = useState(1);
  const [message, setMessage] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const q = quote(l.price_per_night, l.cleaning_fee, nights || 1);
  const own = user?.user_id === l.host_id;
  const { data: open } = useBookingsOpen();
  const closed = open === false && !user?.is_admin;
  const blocked = own || busy || (user && (!nights || clash || !agree));

  const request = async () => {
    if (!user) return startLogin();
    setBusy(true);
    try {
      await api.post("/bookings", { listing_id: l.listing_id, check_in: ISO(range.from), check_out: ISO(range.to), guests, message, agree_terms: agree });
      toast.success("Request sent. You'll pay once the host accepts.");
      navigate("/trips");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sticky top-24 rounded-3xl border bg-white p-6 shadow-xl shadow-stone-900/5" data-testid="booking-card">
      <p className="mb-4"><span className="font-mono-num text-2xl font-semibold">{money0(l.price_per_night)}</span> <span className="text-stone-500">night · no guest fee</span></p>
      <div className="flex justify-center rounded-2xl border">
        <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1} data-testid="booking-calendar" disabled={disabled} />
      </div>
      <p className="mt-2 text-xs text-stone-500" data-testid="booking-selected-dates">{datesLabel(range)}</p>
      <GuestStepper value={guests} max={l.max_guests} onChange={setGuests} />
      <Textarea data-testid="booking-message-input" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Say hi and tell the host about your trip (optional)" className="mt-4 rounded-2xl" rows={2} />
      {clash && <p className="mt-2 text-sm text-red-600" data-testid="booking-date-clash">Some nights in that range are unavailable.</p>}
      {user && !own && !closed && <TermsAgreement agree={agree} setAgree={setAgree} />}
      {closed ? <ComingSoon /> : (
        <>
          {open === false && <p className="mt-4 rounded-xl bg-amber-50 p-2 text-center text-xs font-semibold text-amber-800" data-testid="admin-test-booking-note">Admin test booking: Stripe test mode, hidden from the owner, doesn't block dates</p>}
          <Button data-testid="request-book-button" disabled={blocked || l.tax_zone_ready === false} onClick={request} className="mt-4 h-12 w-full rounded-full text-base">
            {bookButtonLabel({ own, user, busy })}
          </Button>
        </>
      )}
      {l.tax_zone_ready === false && <p className="mt-2 text-center text-xs font-medium text-amber-700" data-testid="booking-tax-zone-pending">Booking opens once this home's tax zone is verified.</p>}
      <p className="mt-2 text-center text-xs text-stone-500">You won't be charged until the host accepts</p>
      <p className="mt-2 text-center text-xs text-stone-500" data-testid="booking-cancellation-policy"><PolicyText policyKey={l.cancellation_policy} /></p>
      {nights > 0 && <div className="mt-6"><GuestBreakdown q={q} price={l.price_per_night} taxRules={l.tax_rules} /></div>}
      {(l.str_license_number || l.room_tax_permit) && (
        <p className="mt-4 text-center text-xs text-stone-500" data-testid="listing-license-details">
          {l.str_license_number && <>Short-term rental license: <span className="font-mono-num">{l.str_license_number}</span></>}
          {l.str_license_number && l.room_tax_permit && " · "}
          {l.room_tax_permit && <>Room tax permit: <span className="font-mono-num">{l.room_tax_permit}</span></>}
        </p>
      )}
    </div>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const { data: l, isLoading, error } = useQuery({ queryKey: ["listing", id], queryFn: () => api.get(`/listings/${id}`).then((r) => r.data) });
  if (isLoading) return <Spinner />;
  if (error) return <Notice icon={SearchX} title="Listing not found" testid="listing-not-found"><Link to="/search" className="text-primary">Browse stays</Link></Notice>;
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold tracking-tight sm:text-4xl" data-testid="listing-title">{l.title}</h1>
      <div className="mb-6 mt-2 flex flex-wrap items-center gap-4 text-sm text-stone-600">
        <Rating value={l.rating} count={l.review_count} testid="listing-rating" />
        <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{l.location}</span>
      </div>
      <Gallery photos={l.photos} title={l.title} />
      <div className="mt-10 grid gap-12 lg:grid-cols-[1fr_400px]">
        <div className="space-y-10">
          <div className="flex flex-wrap gap-6 border-b pb-8 text-sm text-stone-700">
            <span className="inline-flex items-center gap-2"><Users className="h-5 w-5 text-primary" />{l.max_guests} guests</span>
            <span className="inline-flex items-center gap-2"><BedDouble className="h-5 w-5 text-primary" />{l.bedrooms} bedrooms · {l.beds} beds</span>
            <span className="inline-flex items-center gap-2"><Bath className="h-5 w-5 text-primary" />{l.baths} bath{l.baths === 1 ? "" : "s"}</span>
            <span className="rounded-full bg-stone-100 px-3 py-0.5">{l.property_type}</span>
          </div>
          <Link to={`/users/${l.host.user_id}`} className="flex items-center gap-4 rounded-3xl border bg-white p-5 transition-shadow hover:shadow-md" data-testid="listing-host-card">
            <Avatar className="h-14 w-14"><AvatarImage src={imgUrl(l.host.picture)} /><AvatarFallback>{l.host.name?.[0]}</AvatarFallback></Avatar>
            <div>
              <p className="font-semibold">Hosted by {l.host.name}</p>
              <p className="text-sm text-stone-500">{l.host.listing_count} listing{l.host.listing_count > 1 ? "s" : ""} · {l.host.total_reviews} reviews · joined {fmtDate(l.host.created_at)}</p>
            </div>
            <ShieldCheck className="ml-auto h-6 w-6 text-primary" />
          </Link>
          <div>
            <h2 className="mb-3 text-xl font-semibold">About this place</h2>
            <p className="whitespace-pre-line leading-relaxed text-stone-700" data-testid="listing-description">{l.description}</p>
          </div>
          <div>
            <h2 className="mb-4 text-xl font-semibold">What this place offers</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="listing-amenities">
              {l.amenities.map((a) => <span key={a} className="inline-flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-primary" />{a}</span>)}
            </div>
          </div>
          {l.house_rules && (
            <div>
              <h2 className="mb-3 text-xl font-semibold">House rules</h2>
              <p className="whitespace-pre-line rounded-2xl bg-stone-100 p-5 text-sm leading-relaxed text-stone-700" data-testid="listing-house-rules">{l.house_rules}</p>
            </div>
          )}
          <ApproxMap point={l.approx_location} address={l.show_full_address && l.street_address ? `${l.street_address}, ${l.city}, ${l.state} ${l.zip_code}` : ""} />
          <div>
            <h2 className="mb-6 text-xl font-semibold">Reviews</h2>
            {l.reviews.length === 0 ? <p className="text-sm text-stone-500">No reviews yet.</p> : (
              <div className="grid gap-8 md:grid-cols-2" data-testid="listing-reviews">{l.reviews.map((r) => <ReviewItem key={r.review_id} r={r} />)}</div>
            )}
          </div>
        </div>
        <div><BookingCard l={l} /></div>
      </div>
    </div>
  );
}
